/**
 * Question Tracker Service
 * Tracks question IDs shown to prevent repetition (Netflix-style)
 */

import { logger } from '../utils/logger';

interface QuestionTrackingData {
  questionIds: string[];
  testId: string;
  timestamp: string;
}

class QuestionTrackerService {
  private readonly STORAGE_KEY = 'questionTracking';
  private readonly MAX_TRACKED_QUESTIONS = 500; // Limit storage size

  /**
   * Track questions shown in a test
   */
  trackQuestions(testId: string, questionIds: string[]): void {
    try {
      const tracking = this.getTrackingData();
      
      // Add new tracking entry
      tracking.push({
        testId,
        questionIds,
        timestamp: new Date().toISOString()
      });
      
      // Keep only recent entries (limit storage)
      if (tracking.length > 50) {
        tracking.shift(); // Remove oldest
      }
      
      // Flatten all question IDs for quick lookup
      const allQuestionIds = new Set<string>();
      tracking.forEach(entry => {
        entry.questionIds.forEach(id => allQuestionIds.add(id));
      });
      
      // Store tracking data
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tracking));
      
      // Store flattened set for quick lookup
      localStorage.setItem(`${this.STORAGE_KEY}_ids`, JSON.stringify(Array.from(allQuestionIds)));
    } catch (error) {
      logger.error('Failed to track questions:', error);
    }
  }

  /**
   * Get all tracked question IDs
   */
  getTrackedQuestionIds(): Set<string> {
    try {
      const stored = localStorage.getItem(`${this.STORAGE_KEY}_ids`);
      if (!stored) return new Set();
      
      const ids = JSON.parse(stored);
      return new Set(ids);
    } catch (error) {
      return new Set();
    }
  }

  /**
   * Check if a question ID has been shown
   */
  hasBeenShown(questionId: string): boolean {
    const trackedIds = this.getTrackedQuestionIds();
    return trackedIds.has(questionId);
  }

  /**
   * Filter out already-shown questions
   */
  filterUnseenQuestions(questions: Array<{ id: string }>): Array<{ id: string }> {
    const trackedIds = this.getTrackedQuestionIds();
    return questions.filter(q => !trackedIds.has(q.id));
  }

  /**
   * Get tracking data
   */
  private getTrackingData(): QuestionTrackingData[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      return JSON.parse(stored);
    } catch (error) {
      return [];
    }
  }

  /**
   * Clear tracking (for testing or reset)
   */
  clearTracking(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(`${this.STORAGE_KEY}_ids`);
  }

  /**
   * Get questions shown for a specific test
   */
  getQuestionsForTest(testId: string): string[] {
    const tracking = this.getTrackingData();
    const testEntries = tracking.filter(entry => entry.testId === testId);
    const questionIds = new Set<string>();
    
    testEntries.forEach(entry => {
      entry.questionIds.forEach(id => questionIds.add(id));
    });
    
    return Array.from(questionIds);
  }
}

export const questionTracker = new QuestionTrackerService();

























